/**
 * 加密相关
 */

import { secureSession } from '@/libs/secureSession';

/**
 * 使用同一把 AES 会话密钥加密单个字段，供单字段和多字段请求复用。
 */
const encryptWithAesKey = async (plainText: string, aesKey: CryptoKey): Promise<string> => {
    // 随机 IV（16 字节）
    const iv = window.crypto.getRandomValues(new Uint8Array(16));

    const encoder = new TextEncoder();
    const data = encoder.encode(plainText);
    console.log('encryptField data:', data);

    const ciphertext = await window.crypto.subtle.encrypt(
        {
            name: 'AES-CBC',
            iv,
        },
        aesKey,
        data,
    );

    // 转 Base64
    return window.btoa(`${String.fromCharCode(...iv)}${String.fromCharCode(...new Uint8Array(ciphertext))}`);
};

/**
 * 加密单个敏感字段（返回 iv:ciphertext 格式）
 * @param plainText
 */
// eslint-disable-next-line import/prefer-default-export
export const encryptField = async (plainText: string): Promise<{
    encrypted: string;
    sessionKeyId: string;
}> => {
    try {
        const { aesKey, sessionKeyId } = await secureSession.getAvailableSession();

        return {
            encrypted: await encryptWithAesKey(plainText, aesKey),
            sessionKeyId,
        };
    } catch (e) {
        // todo 日志上报错误信息
        return {
            encrypted: plainText,
            sessionKeyId: '',
        };
    }
};

/**
 * 加密请求数据中的指定字段；同一请求内的字段共用会话密钥和 `encryption_session_id`。
 * 缺失字段不会补入请求，加密失败时沿用旧版策略回退原始数据。
 */
export const encryptRequestFields = async <T extends object>(
    data: T,
    fields: string[],
): Promise<T & { encryption_session_id?: string }> => {
    // 字段名由接口契约动态指定，需要索引类型承接复制后的请求数据。
    const requestData = { ...data };
    const indexedRequestData = requestData as Record<string, unknown> & { encryption_session_id?: string };
    const targetFields = fields.filter((field) => (
        field in indexedRequestData &&
        indexedRequestData[field] !== undefined &&
        indexedRequestData[field] !== null
    ));

    if (!targetFields.length) {
        return requestData;
    }

    try {
        const { aesKey, sessionKeyId } = await secureSession.getAvailableSession();
        if (!sessionKeyId) {
            return requestData;
        }
        const encryptedValues = await Promise.all(
            targetFields.map((field) => encryptWithAesKey(String(indexedRequestData[field]), aesKey)),
        );

        targetFields.forEach((field, index) => {
            indexedRequestData[field] = encryptedValues[index];
        });
        indexedRequestData.encryption_session_id = sessionKeyId;
    } catch {
        // 保持现有接口兼容策略：加密失败时回退明文请求，由后端继续处理。
    }

    return requestData;
};
